import React from 'react';
import SEO from '../components/SEO';
import ServicesGrid from '../components/ServicesGrid';
import PricingTiers from '../components/PricingTiers';
import ToolsMarquee from '../components/ToolsMarquee';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { Zap, MailCheck, RefreshCw, TrendingUp, MessageCircle, Repeat, ArrowUpRight } from 'lucide-react';
import { services } from '../mock';

const iconMap = {
  zap: Zap,
  'mail-check': MailCheck,
  'refresh-cw': RefreshCw,
  'trending-up': TrendingUp,
  'message-circle': MessageCircle,
  repeat: Repeat,
};

const Services = () => {
  return (
    <>
      <SEO
        title="Services — Email Strategy, Build, Migration & Retainers | Growth Hacking"
        description="Six battle-tested email-marketing engagements: full system builds, deliverability rescues, Kit.com migrations, newsletter monetization, ManyChat funnels and retainers."
        canonical="/services"
      />
      <main className="bg-cream pt-28">
        {/* Hero */}
        <section className="container-x section pt-12" data-testid="services-hero">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Services
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                Six engagements,{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">one north star</span>:
                revenue per subscriber.
              </h1>
            </div>
            <p className="lg:col-span-4 text-lg text-muted-warm">
              Pick what you need now. Combine inside a retainer when you&apos;re ready to
              compound.
            </p>
          </div>
        </section>

        <ServicesGrid />

        {/* Detailed alternating service blocks */}
        <section className="container-x section" data-testid="services-detailed">
          <div className="space-y-20">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon] || Zap;
              const reverse = i % 2 === 1;
              return (
                <div
                  key={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}
                >
                  <div className="lg:col-span-7 [direction:ltr]">
                    <span className="text-xs uppercase tracking-[0.25em] text-muted-warm font-semibold">
                      0{s.id} · Engagement
                    </span>
                    <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3 text-ink leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-5 text-base md:text-lg text-muted-warm leading-relaxed">
                      {s.description}
                    </p>
                    <p className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] text-sm font-semibold">
                      Outcome · {s.benefit}
                    </p>
                  </div>
                  <div className="lg:col-span-5 [direction:ltr]">
                    <div className="rounded-[1.75rem] bg-cream-2 border border-line p-8 md:p-10 relative overflow-hidden">
                      <Icon className="w-10 h-10 text-[var(--gh-orange)]" />
                      <p className="mt-6 font-display text-2xl font-bold text-ink">What you get</p>
                      <ul className="mt-4 space-y-2 text-sm text-muted-warm">
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)]" />
                          A scoped statement of work in 48 hours
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)]" />
                          A weekly Loom + dashboard review
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)]" />
                          A 30-day rollback plan if anything regresses
                        </li>
                      </ul>
                      <a
                        href="/contact"
                        data-testid={`service-detail-${s.id}-cta`}
                        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-[var(--gh-orange)] transition-colors"
                      >
                        Discuss this engagement <ArrowUpRight className="w-4 h-4" />
                      </a>
                      <div
                        aria-hidden
                        className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-[var(--gh-orange)] opacity-10 blur-3xl"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <ToolsMarquee />
        <PricingTiers />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
};

export default Services;
